import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Beaker,
    AlertTriangle,
    ClipboardList,
    LineChart,
    Activity,
    Repeat,
    BarChart3,
    SlidersHorizontal,
    Sigma,
} from "lucide-react";

import PageShell from "@/components/site/PageShell";
import HeroSchematic from "@/components/site/HeroSchematic";
import DashboardMockup from "@/components/site/DashboardMockup";
import PricingCards from "@/components/site/PricingCards";
import LifecycleBar from "@/components/site/LifecycleBar";
import MaturityDiagram from "@/components/site/MaturityDiagram";
import { useRegion, VALID_REGIONS } from "@/contexts/RegionContext";

/* ---------- shared atoms ---------- */

const SectionLabel = ({ index, title, testid }) => (
    <div data-testid={testid} className="flex items-baseline gap-6 mb-10">
        <span className="tc-section-index">{index}</span>
        <div className="flex-1 h-px bg-[#d8dcdd]" />
        <h2 className="text-[22px] sm:text-[26px] lg:text-[30px] font-medium tracking-tight text-[#0e1416]">
            {title}
        </h2>
    </div>
);

const RoadmapPill = () => (
    <span
        data-testid="roadmap-pill"
        className="inline-flex items-center tc-mono text-[9.5px] tracking-[0.16em] uppercase text-[#01696f] border border-[#01696f]/40 px-1.5 py-[2px] leading-none"
    >
        Roadmap
    </span>
);

const DecisionCard = ({ Icon, title, body, testid, roadmap }) => (
    <div
        data-testid={testid}
        className="border-t border-[#d8dcdd] pt-5 pr-2 flex flex-col gap-3 group"
    >
        <div className="flex items-center justify-between">
            <Icon size={16} strokeWidth={1.5} className="text-[#0e1416]" />
            {roadmap && <RoadmapPill />}
        </div>
        <h3 className="text-[14.5px] font-medium tracking-tight text-[#0e1416]">
            {title}
        </h3>
        <p className="text-[12.5px] text-[#5a6669] leading-[1.6]">{body}</p>
        {roadmap && (
            <span className="tc-mono text-[10.5px] text-[#8a9498] tracking-wider mt-1">
                In design — not yet shipped.
            </span>
        )}
    </div>
);

const SpecRow = ({ left, right, last }) => (
    <div
        className={`flex items-baseline justify-between py-3.5 ${
            last ? "" : "border-b border-[#d8dcdd]"
        }`}
    >
        <span className="tc-mono text-[12px] text-[#0e1416] font-medium">
            {left}
        </span>
        <span className="text-[12px] text-[#5a6669] text-right max-w-[60%] leading-[1.5]">
            {right}
        </span>
    </div>
);

/* ---------- Landing ---------- */

const NEUTRAL_FOOTER =
    "Standards-aware across HK CIC · US ACI/ASTM/CSA · EU EN 13670 + BAW · UK BS EN 13670 + BS 1881-207 + CIRIA C766 · AU Austroads ATS 5321.";

export default function Landing() {
    const {
        region,
        catalogue,
        regionData,
        currency,
        pricing,
        detectionSource,
    } = useRegion();

    const pricingCaption = "Prices in USD.";

    // Build a stable ordered list of region rows (HK, US, EU, UK, AU)
    const regionRows = VALID_REGIONS
        .map((code) => ({ code, entry: catalogue[code] }))
        .filter((r) => r.entry);

    return (
        <PageShell testid="landing-page">
            {/* ========== HERO ========== */}
            <section
                data-testid="hero-section"
                className="border-b border-[#d8dcdd]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
                        <div className="lg:col-span-7">
                            <div
                                data-testid="hero-eyebrow"
                                className="tc-eyebrow text-[#01696f] mb-5"
                            >
                                Concrete Control
                            </div>
                            <h1
                                data-testid="hero-headline"
                                className="text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.08] tracking-[-0.018em] font-medium text-[#0e1416]"
                            >
                                Predict cracking risk. Track early-age strength.{" "}
                                <span className="text-[#5a6669]">
                                    Pour with certainty.
                                </span>
                            </h1>
                            <p
                                data-testid="hero-subtext"
                                className="mt-6 text-[14.5px] sm:text-[15.5px] text-[#5a6669] leading-[1.65] max-w-[68ch]"
                            >
                                Concrete Control is a platform for engineers,
                                contractors, and project teams — a unified
                                workflow for thermal cracking prevention,
                                maturity-based in-place strength, milestone
                                control, and ongoing concrete QA/QC analytics.
                            </p>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                                <Link
                                    to="/app"
                                    data-testid="hero-cta-launch-app"
                                    className="group inline-flex items-center justify-between sm:justify-start gap-3 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-5 py-3 text-[13.5px] font-medium transition-colors duration-200"
                                >
                                    <span>Launch App</span>
                                    <ArrowUpRight
                                        size={15}
                                        strokeWidth={1.5}
                                        className="transition-transform duration-200 group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
                                    />
                                </Link>
                                <Link
                                    to="/demo"
                                    data-testid="hero-cta-request-demo"
                                    className="group inline-flex items-center justify-between sm:justify-start gap-3 border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416] px-5 py-3 text-[13.5px] font-medium transition-colors duration-200"
                                >
                                    <span>Request a demo</span>
                                    <ArrowUpRight
                                        size={15}
                                        strokeWidth={1.5}
                                        className="transition-transform duration-200 group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
                                    />
                                </Link>
                            </div>

                            <p
                                data-testid="hero-metadata"
                                className="mt-9 tc-mono text-[10.5px] text-[#5a6669] tracking-wider leading-[1.55]"
                            >
                                Thermal control · Maturity (Nurse-Saul ·
                                Arrhenius) · Monitoring · Field calibration ·
                                QC analytics (roadmap)
                            </p>
                        </div>

                        <div className="lg:col-span-5 flex justify-center lg:justify-end">
                            <HeroSchematic />
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== 01 LIFECYCLE ========== */}
            <section
                id="platform"
                data-testid="section-platform"
                className="border-b border-[#d8dcdd]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="01"
                        title="The lifecycle — one connected workflow"
                        testid="section-platform-label"
                    />

                    <p className="max-w-[72ch] text-[15.5px] text-[#0e1416] leading-[1.65] mb-12">
                        One workflow. Multiple decisions from the same thermal
                        and maturity data. Mix validation, thermal control,
                        cracking risk, maturity-based strength, monitoring, and
                        next-pour feedback share a single data spine — so a
                        prediction made before the pour and a measurement made
                        during curing pass through the same calibration curve.
                    </p>

                    <LifecycleBar />
                </div>
            </section>

            {/* ========== 02 MATURITY METHOD ========== */}
            <section
                id="maturity"
                data-testid="section-maturity"
                className="border-b border-[#d8dcdd] bg-[#eef0ef]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="02"
                        title="From temperature to strength"
                        testid="section-maturity-label"
                    />

                    <p className="max-w-[72ch] text-[15.5px] text-[#0e1416] leading-[1.65] mb-10">
                        A temperature-time history — predicted before the pour
                        or measured during curing — is converted to maturity.
                        Maturity maps to in-place strength through a
                        mix-specific calibration curve.{" "}
                        <span className="font-medium">
                            The relationship is not universal: it must be
                            calibrated and validated for the actual concrete
                            mix.
                        </span>
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                        <div className="lg:col-span-7">
                            <MaturityDiagram />
                        </div>

                        <div className="lg:col-span-5 flex flex-col gap-6">
                            <div
                                data-testid="method-card"
                                className="bg-[#ffffff] border border-[#d8dcdd] p-6"
                            >
                                <div className="tc-eyebrow text-[#5a6669] mb-4">
                                    Methods supported
                                </div>
                                <SpecRow
                                    left="Nurse-Saul"
                                    right="Linear maturity at reference temperature T_ref. Standard, widely accepted, simple to calibrate."
                                />
                                <SpecRow
                                    left="Arrhenius · t_e"
                                    right="Activation-energy-based, mix-specific E_a. Recommended for blended binders and varying curing temperatures."
                                    last
                                />
                            </div>

                            <p className="text-[13.5px] text-[#5a6669] leading-[1.65]">
                                Concrete Control applies the same maturity
                                engine to both predicted and monitored
                                temperature histories, so pre-pour predictions
                                and in-place curing estimates use a single
                                calibrated curve per mix.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== 03 DECISIONS ========== */}
            <section
                id="workflows"
                data-testid="section-workflows"
                className="border-b border-[#d8dcdd]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="03"
                        title="Decisions the suite supports"
                        testid="section-workflows-label"
                    />

                    <p className="max-w-[64ch] text-[14.5px] text-[#5a6669] leading-[1.65] mb-12">
                        Seven decisions, one engine. Each is grounded in the
                        same thermal and maturity data, calibrated per mix and
                        traceable for QA review. The seventh — ongoing QC/QA
                        analytics — is on the near-term roadmap.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
                        <DecisionCard
                            Icon={Beaker}
                            title="Mix suitability"
                            body="Will this mix and pour setup hit the target temperature profile and ΔT envelope?"
                            testid="decision-mix-suitability"
                        />
                        <DecisionCard
                            Icon={AlertTriangle}
                            title="Cracking risk"
                            body="Early-age thermal cracking likelihood via tensile stress and cracking index factor (CIF)."
                            testid="decision-cracking-risk"
                        />
                        <DecisionCard
                            Icon={ClipboardList}
                            title="Thermal control plan"
                            body="Monitoring layout and decision thresholds defined before pour day."
                            testid="decision-thermal-control"
                        />
                        <DecisionCard
                            Icon={LineChart}
                            title="Predicted in-place strength"
                            body="Equivalent-age-based strength prediction from the simulated temperature history."
                            testid="decision-predicted-strength"
                        />
                        <DecisionCard
                            Icon={Activity}
                            title="Monitored in-place strength"
                            body="Same maturity engine driven by measured site temperatures, with milestone tracking for formwork release and PT stressing."
                            testid="decision-monitored-strength"
                        />
                        <DecisionCard
                            Icon={Repeat}
                            title="Next-pour calibration"
                            body="Diagnostic deltas between predicted and measured behavior, feeding the next pour in the sequence."
                            testid="decision-next-pour"
                        />
                        <DecisionCard
                            Icon={BarChart3}
                            title="Ongoing QC/QA analytics"
                            body="CUSUM, Shewhart charts, and Bayesian estimation of 28-day strength from 7-day cube tests."
                            testid="decision-qcqa-analytics"
                            roadmap
                        />
                    </div>
                </div>
            </section>

            {/* ========== 04 STANDARDS-AWARE MARKETS ========== */}
            <section
                id="markets"
                data-testid="section-markets"
                className="border-b border-[#d8dcdd] bg-[#eef0ef]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="04"
                        title="Standards-aware across major concrete markets"
                        testid="section-markets-label"
                    />

                    <p className="max-w-[78ch] text-[15.5px] text-[#0e1416] leading-[1.65] mb-10">
                        Concrete Control adapts wording, currency, and
                        standards references to your region. The underlying
                        engineering — thermal solver, maturity engine,
                        calibration, yield — remains universal and
                        configurable.
                    </p>

                    <div
                        data-testid="markets-table"
                        className="border border-[#d8dcdd] bg-[#ffffff]"
                    >
                        {/* Header */}
                        <div className="hidden md:grid grid-cols-12 px-5 py-3 border-b border-[#d8dcdd] bg-[#f6f7f6]">
                            <div className="col-span-3 tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">
                                Region
                            </div>
                            <div className="col-span-2 tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">
                                Currency
                            </div>
                            <div className="col-span-7 tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">
                                Standards referenced
                            </div>
                        </div>
                        {regionRows.map(({ code, entry }) => {
                            const selected = code === region;
                            return (
                                <div
                                    key={code}
                                    data-testid={`markets-row-${code}`}
                                    className={`relative grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-0 px-5 py-4 md:items-baseline ${
                                        selected
                                            ? "bg-[rgba(1,105,111,0.05)]"
                                            : ""
                                    } border-b border-[#d8dcdd] last:border-b-0`}
                                >
                                    {selected && (
                                        <span
                                            aria-hidden="true"
                                            data-testid={`markets-row-${code}-current`}
                                            className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#01696f]"
                                        />
                                    )}
                                    <div className="md:col-span-3 text-[13.5px] text-[#0e1416] font-medium">
                                        {entry.label}
                                    </div>
                                    <div className="md:col-span-2 tc-mono text-[12px] text-[#0e1416]">
                                        {entry.currency}{" "}
                                        <span className="text-[#8a9498]">
                                            {entry.currency_symbol}
                                        </span>
                                    </div>
                                    <div className="md:col-span-7 tc-mono text-[11.5px] text-[#5a6669] leading-[1.6]">
                                        {(entry.standards || [])
                                            .map((s) => s.code)
                                            .join(", ")}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-5 tc-mono text-[11px] text-[#5a6669] tracking-wider leading-[1.55]">
                        Use the region picker in the nav to switch context.
                        Pricing and standards adjust; the engineering does not.
                    </p>
                </div>
            </section>

            {/* ========== 05 FORTHCOMING — QC/QA ANALYTICS ========== */}
            <section
                id="roadmap"
                data-testid="section-roadmap"
                className="border-b border-[#d8dcdd]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="05"
                        title="Forthcoming · Concrete QC/QA analytics"
                        testid="section-roadmap-label"
                    />

                    <p className="max-w-[72ch] tc-mono text-[13px] italic text-[#5a6669] leading-[1.7] mb-12">
                        Concrete Control is being extended into a full QC/QA
                        platform for concrete. The next analytic surfaces are
                        in design and will appear here as they ship.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d8dcdd] border border-[#d8dcdd]">
                        <RoadmapItem
                            Icon={LineChart}
                            title="CUSUM charts"
                            body="Ongoing strength stability monitoring for batch sequences."
                            testid="roadmap-cusum"
                        />
                        <RoadmapItem
                            Icon={SlidersHorizontal}
                            title="Shewhart charts"
                            body="Batch-level acceptance and process-control limits."
                            testid="roadmap-shewhart"
                        />
                        <RoadmapItem
                            Icon={Sigma}
                            title="Bayesian 28-day strength estimation"
                            body="Predict 28-day compressive strength from 7-day cube test data with uncertainty bounds."
                            testid="roadmap-bayesian"
                        />
                    </div>
                </div>
            </section>

            {/* ========== 06 WORKSPACE PREVIEW ========== */}
            <section
                id="feature-preview"
                data-testid="section-feature-preview"
                className="border-b border-[#d8dcdd] bg-[#eef0ef]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="06"
                        title="Workspace preview"
                        testid="section-feature-preview-label"
                    />

                    <p className="max-w-[68ch] text-[14.5px] text-[#5a6669] leading-[1.65] mb-10">
                        Three engineering panels on one workspace: a thermal
                        contour, a temperature-time + maturity dual chart, and
                        an equivalent-age vs strength curve with calibration
                        points and a current-time marker.
                    </p>

                    <DashboardMockup />

                    <p
                        data-testid="feature-preview-caption"
                        className="mt-4 tc-mono text-[11px] text-[#5a6669] tracking-wider"
                    >
                        Workspace overview — thermal, maturity, and strength on
                        one panel.
                    </p>
                </div>
            </section>

            {/* ========== 07 PRICING ========== */}
            <section
                id="pricing"
                data-testid="section-pricing"
                className="border-b border-[#d8dcdd]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel
                        index="07"
                        title="Pricing"
                        testid="section-pricing-label"
                    />

                    <p className="max-w-[64ch] text-[14.5px] text-[#5a6669] leading-[1.65] mb-12">
                        Three tiers. Demo for evaluation, Starter for
                        production pours, Professional for live monitoring and
                        inverse calibration. Prices are the same in every
                        region — no local-currency conversion.
                    </p>

                    <PricingCards idAttr="landing" />

                    <p
                        data-testid="pricing-currency-caption"
                        className="mt-5 tc-mono text-[11px] text-[#5a6669] tracking-wider leading-[1.55]"
                    >
                        {pricingCaption}
                    </p>
                </div>
            </section>

            {/* ========== 08 ABOUT ========== */}
            <section
                id="about"
                data-testid="section-about"
                className="border-b border-[#d8dcdd]"
            >
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-20 lg:py-24">
                    <SectionLabel index="08" title="About" testid="section-about-label" />
                    <p className="max-w-[64ch] text-[14.5px] text-[#5a6669] leading-[1.65] mb-10">
                        Concrete Control is a global platform for concrete
                        cracking prevention, maturity-based strength, and
                        pour-time decisions.
                    </p>
                    <div
                        data-testid="about-legal-block"
                        className="tc-mono text-[12px] text-[#0e1416] leading-[1.85] border-t border-b border-[#d8dcdd] py-6 max-w-[52ch]"
                    >
                        Concrete Control LLC<br />
                        Registration pending — New Mexico<br />
                        1209 Mountain Road Place Northeast<br />
                        Albuquerque, New Mexico 87110<br />
                        <a
                            href="https://www.concrete-control.com"
                            className="hover:text-[#01696f] transition-colors"
                        >
                            www.concrete-control.com
                        </a>
                    </div>
                </div>
            </section>

            {/* Documentation anchor (footer-link target) */}
            <section
                id="documentation"
                data-testid="section-documentation"
                aria-hidden="true"
                className="sr-only"
            >
                Documentation
            </section>
        </PageShell>
    );
}

const RoadmapItem = ({ Icon, title, body, testid }) => (
    <div
        data-testid={testid}
        className="bg-[#ffffff] p-7 lg:p-8 flex flex-col gap-4"
    >
        <div className="flex items-center justify-between">
            <Icon size={16} strokeWidth={1.5} className="text-[#0e1416]" />
            <RoadmapPill />
        </div>
        <h3 className="text-[14.5px] font-medium tracking-tight text-[#0e1416]">
            {title}
        </h3>
        <p className="text-[13px] text-[#5a6669] leading-[1.6]">{body}</p>
    </div>
);

// Exported for the Footer to keep one source of truth on the neutral string.
export { NEUTRAL_FOOTER };
